/* ─── Miembro del chat ─── */
export type ChatRole = 'admin' | 'mod' | 'host' | 'listener';

export interface ChatMember {
  id: number;
  name: string;
  username: string;
  avatarUrl: string;
  role: ChatRole;
  isOnline: boolean;
  isTyping: boolean;
  isBlocked: boolean;
}

export const memberRoleLabels: Record<ChatRole, string> = {
  admin: 'Admin',
  mod: 'Mod',
  host: 'Locutor',
  listener: 'Oyente',
};

export const memberRoleColors: Record<ChatRole, string> = {
  admin: 'text-rose-500',
  mod: 'text-emerald-500',
  host: 'text-amber-500',
  listener: 'text-foreground-400',
};

/* ─── Canal ─── */
export type ChannelType = 'text' | 'voice';

export interface ChatChannel {
  id: number;
  name: string;
  type: ChannelType;
  unreadCount: number;
  description: string;
}

export interface ChannelCategory {
  id: number;
  name: string;
  channels: ChatChannel[];
}

/* ─── Reacción ─── */
export type ReactionEmoji = '👍' | '❤️' | '😂' | '🔥' | '👏' | '😮' | '😢' | '🙌';

export interface ChatReaction {
  emoji: ReactionEmoji;
  count: number;
  users: string[];
  reactedByMe: boolean;
}

/* ─── Mensaje ─── */
export interface ChatMessage {
  id: number;
  channelId: number;
  userId: number;
  userName: string;
  userAvatar: string;
  userRole: ChatRole;
  content: string;
  timestamp: string;
  relativeTime: string;
  reactions: ChatReaction[];
  replyTo: number | null;
  replyPreview: string | null;
  replyUserName: string | null;
  isPinned: boolean;
  isHighlighted: boolean;
  edited: boolean;
}

/* ─── Canales ─── */
export const channelCategories: ChannelCategory[] = [
  {
    id: 1,
    name: 'GENERAL',
    channels: [
      { id: 1, name: 'bienvenida', type: 'text', unreadCount: 0, description: 'Presentate y conocé a la comunidad' },
      { id: 2, name: 'general', type: 'text', unreadCount: 3, description: 'Charlá de lo que quieras con la comunidad' },
      { id: 3, name: 'anuncios', type: 'text', unreadCount: 0, description: 'Novedades y anuncios de la radio' },
    ],
  },
  {
    id: 2,
    name: 'AL AIRE',
    channels: [
      { id: 4, name: 'en-vivo', type: 'text', unreadCount: 12, description: 'Comentá el programa en vivo' },
      { id: 5, name: 'pedidos-musicales', type: 'text', unreadCount: 5, description: 'Pedí canciones y dedicatorias' },
      { id: 6, name: 'concursos', type: 'text', unreadCount: 2, description: 'Participá de los concursos al aire' },
    ],
  },
  {
    id: 3,
    name: 'COMUNIDAD',
    channels: [
      { id: 7, name: 'musica', type: 'text', unreadCount: 0, description: 'Compartí y descubrí música nueva' },
      { id: 8, name: 'eventos', type: 'text', unreadCount: 1, description: 'Eventos, recitales y juntadas' },
      { id: 9, name: 'off-topic', type: 'text', unreadCount: 0, description: 'De todo un poco, lo que no va en otros canales' },
    ],
  },
];

/* ─── Miembros ─── */
export const chatMembers: ChatMember[] = [
  { id: 1, name: 'Carlos Herrera', username: '@carlosherrera', avatarUrl: '', role: 'admin', isOnline: true, isTyping: false, isBlocked: false },
  { id: 2, name: 'Valentina Suárez', username: '@valesuarez', avatarUrl: '', role: 'host', isOnline: true, isTyping: false, isBlocked: false },
  { id: 3, name: 'Roberto Díaz', username: '@robertodiaz', avatarUrl: '', role: 'host', isOnline: true, isTyping: true, isBlocked: false },
  { id: 4, name: 'Diego Martínez', username: '@diegomartinez', avatarUrl: '', role: 'mod', isOnline: true, isTyping: false, isBlocked: false },
  { id: 5, name: 'Lucía Fernández', username: '@luciafernandez', avatarUrl: '', role: 'host', isOnline: false, isTyping: false, isBlocked: false },
  { id: 6, name: 'Sofía Ramírez', username: '@sofiram', avatarUrl: '', role: 'listener', isOnline: true, isTyping: false, isBlocked: false },
  { id: 7, name: 'Camila Torres', username: '@camitorr', avatarUrl: '', role: 'listener', isOnline: true, isTyping: false, isBlocked: false },
  { id: 8, name: 'Martín Gutiérrez', username: '@martingut', avatarUrl: '', role: 'listener', isOnline: true, isTyping: false, isBlocked: false },
  { id: 9, name: 'Laura Castillo', username: '@laucast', avatarUrl: '', role: 'listener', isOnline: false, isTyping: false, isBlocked: false },
  { id: 10, name: 'Andrés López', username: '@andrelop', avatarUrl: '', role: 'listener', isOnline: true, isTyping: false, isBlocked: false },
  { id: 11, name: 'Carlos Mendoza', username: '@cmendoza', avatarUrl: '', role: 'listener', isOnline: false, isTyping: false, isBlocked: false },
  { id: 12, name: 'Diego Fernández', username: '@diegof', avatarUrl: '', role: 'listener', isOnline: true, isTyping: false, isBlocked: false },
  { id: 13, name: 'Valentina Ríos', username: '@valerios', avatarUrl: '', role: 'listener', isOnline: false, isTyping: false, isBlocked: false },
  { id: 14, name: 'Pedro Gutiérrez', username: '@pedrogutierrez', avatarUrl: '', role: 'listener', isOnline: true, isTyping: false, isBlocked: false },
  { id: 15, name: 'Javier Méndez', username: '@javiermendez', avatarUrl: '', role: 'listener', isOnline: false, isTyping: false, isBlocked: false },
  { id: 16, name: 'María José López', username: '@mariajose', avatarUrl: '', role: 'mod', isOnline: true, isTyping: false, isBlocked: false },
  { id: 17, name: 'Andrés Vega', username: '@andresvega', avatarUrl: '', role: 'host', isOnline: false, isTyping: false, isBlocked: false },
  { id: 18, name: 'SpamBot_2024', username: '@spambot', avatarUrl: '', role: 'listener', isOnline: false, isTyping: false, isBlocked: true },
];

/* ─── Mensajes del canal general (id=2) ─── */
export const chatMessages: ChatMessage[] = [
  {
    id: 1, channelId: 2, userId: 2, userName: 'Valentina Suárez', userAvatar: '', userRole: 'host',
    content: '¡Buenas tardes a toda la comunidad! 🎙️ Ya estamos al aire con Tardes de Pop. ¿Cómo andan?',
    timestamp: '2026-06-18T14:00:00', relativeTime: 'Hace 2 h',
    reactions: [
      { emoji: '❤️', count: 8, users: ['Sofía Ramírez', 'Camila Torres', 'Martín Gutiérrez'], reactedByMe: false },
      { emoji: '🔥', count: 5, users: ['Andrés López', 'Laura Castillo'], reactedByMe: false },
      { emoji: '👏', count: 3, users: ['Diego Fernández'], reactedByMe: false },
    ],
    replyTo: null, replyPreview: null, replyUserName: null,
    isPinned: true, isHighlighted: false, edited: false,
  },
  {
    id: 2, channelId: 2, userId: 7, userName: 'Camila Torres', userAvatar: '', userRole: 'listener',
    content: '¡Acá andamos Vale! Escuchando desde Rosario. Pongan algo de Dua Lipa porfa 🙏',
    timestamp: '2026-06-18T14:02:00', relativeTime: 'Hace 2 h',
    reactions: [
      { emoji: '👍', count: 4, users: ['Sofía Ramírez', 'Martín Gutiérrez'], reactedByMe: false },
      { emoji: '❤️', count: 2, users: ['Valentina Suárez'], reactedByMe: false },
    ],
    replyTo: null, replyPreview: null, replyUserName: null,
    isPinned: false, isHighlighted: false, edited: false,
  },
  {
    id: 3, channelId: 2, userId: 6, userName: 'Sofía Ramírez', userAvatar: '', userRole: 'listener',
    content: 'Yo también voto por Dua Lipa. La rompió en el último disco 💿',
    timestamp: '2026-06-18T14:03:00', relativeTime: 'Hace 2 h',
    reactions: [],
    replyTo: 2, replyPreview: '¡Acá andamos Vale! Escuchando desde Rosario...', replyUserName: 'Camila Torres',
    isPinned: false, isHighlighted: false, edited: false,
  },
  {
    id: 4, channelId: 2, userId: 2, userName: 'Valentina Suárez', userAvatar: '', userRole: 'host',
    content: '¡Dua Lipa va ahora mismo! Camila y Sofi, esto es para ustedes 🎵 Dance The Night',
    timestamp: '2026-06-18T14:05:00', relativeTime: 'Hace 2 h',
    reactions: [
      { emoji: '🔥', count: 12, users: ['Camila Torres', 'Sofía Ramírez', 'Martín Gutiérrez', 'Andrés López', 'Laura Castillo'], reactedByMe: false },
      { emoji: '🙌', count: 6, users: ['Pedro Gutiérrez', 'Diego Fernández'], reactedByMe: false },
      { emoji: '❤️', count: 9, users: ['Camila Torres', 'Sofía Ramírez', 'María José López'], reactedByMe: false },
    ],
    replyTo: null, replyPreview: null, replyUserName: null,
    isPinned: false, isHighlighted: true, edited: false,
  },
  {
    id: 5, channelId: 2, userId: 8, userName: 'Martín Gutiérrez', userAvatar: '', userRole: 'listener',
    content: 'Temazo. Los viernes tendrían que hacer una sección de clásicos de los 80, se extraña esa música en la radio.',
    timestamp: '2026-06-18T14:08:00', relativeTime: 'Hace 1.5 h',
    reactions: [
      { emoji: '👍', count: 7, users: ['Andrés López', 'Laura Castillo', 'Diego Fernández'], reactedByMe: false },
      { emoji: '❤️', count: 3, users: ['Carlos Mendoza'], reactedByMe: false },
    ],
    replyTo: null, replyPreview: null, replyUserName: null,
    isPinned: false, isHighlighted: false, edited: false,
  },
  {
    id: 6, channelId: 2, userId: 4, userName: 'Diego Martínez', userAvatar: '', userRole: 'mod',
    content: 'Buena idea Martín. Lo charlamos en la reunión de equipo del lunes. Mientras tanto, mantengamos el respeto en el chat. Todo comentario es bienvenido siempre que sea con buena onda ✌️',
    timestamp: '2026-06-18T14:10:00', relativeTime: 'Hace 1.5 h',
    reactions: [
      { emoji: '👏', count: 5, users: ['Valentina Suárez', 'Sofía Ramírez'], reactedByMe: false },
    ],
    replyTo: 5, replyPreview: 'Temazo. Los viernes tendrían que hacer una sección de clásicos...', replyUserName: 'Martín Gutiérrez',
    isPinned: false, isHighlighted: false, edited: false,
  },
  {
    id: 7, channelId: 2, userId: 10, userName: 'Andrés López', userAvatar: '', userRole: 'listener',
    content: 'Aprovecho para pedir el nuevo tema de Babasónicos. Lo vengo pidiendo hace semanas jaja, no me fallen 🙏',
    timestamp: '2026-06-18T14:15:00', relativeTime: 'Hace 1.5 h',
    reactions: [
      { emoji: '😂', count: 4, users: ['Camila Torres', 'Martín Gutiérrez'], reactedByMe: false },
      { emoji: '👍', count: 2, users: ['Sofía Ramírez'], reactedByMe: false },
    ],
    replyTo: null, replyPreview: null, replyUserName: null,
    isPinned: false, isHighlighted: false, edited: false,
  },
  {
    id: 8, channelId: 2, userId: 3, userName: 'Roberto Díaz', userAvatar: '', userRole: 'host',
    content: 'Andrés, ya lo tengo en la lista. Va en el próximo bloque, en unos 15 minutos. Aguante Babasónicos 🎸',
    timestamp: '2026-06-18T14:16:00', relativeTime: 'Hace 1.5 h',
    reactions: [
      { emoji: '🔥', count: 8, users: ['Andrés López', 'Sofía Ramírez', 'Camila Torres', 'Martín Gutiérrez'], reactedByMe: false },
      { emoji: '🙌', count: 4, users: ['Diego Fernández', 'Pedro Gutiérrez'], reactedByMe: false },
    ],
    replyTo: 7, replyPreview: 'Aprovecho para pedir el nuevo tema de Babasónicos...', replyUserName: 'Andrés López',
    isPinned: false, isHighlighted: false, edited: false,
  },
  {
    id: 9, channelId: 2, userId: 12, userName: 'Diego Fernández', userAvatar: '', userRole: 'listener',
    content: 'Che alguien sabe si el finde hay algún evento? Me re coparía una juntada de oyentes',
    timestamp: '2026-06-18T14:25:00', relativeTime: 'Hace 1 h',
    reactions: [
      { emoji: '👍', count: 3, users: ['Pedro Gutiérrez', 'Andrés López'], reactedByMe: false },
    ],
    replyTo: null, replyPreview: null, replyUserName: null,
    isPinned: false, isHighlighted: false, edited: false,
  },
  {
    id: 10, channelId: 2, userId: 6, userName: 'Sofía Ramírez', userAvatar: '', userRole: 'listener',
    content: 'Diego, fijate en el canal #eventos. El sábado hay meet & greet con los locutores en el Parque Central. Creo que todavía hay lugar.',
    timestamp: '2026-06-18T14:27:00', relativeTime: 'Hace 1 h',
    reactions: [
      { emoji: '❤️', count: 2, users: ['Diego Fernández'], reactedByMe: false },
    ],
    replyTo: 9, replyPreview: 'Che alguien sabe si el finde hay algún evento?...', replyUserName: 'Diego Fernández',
    isPinned: false, isHighlighted: false, edited: false,
  },
  {
    id: 11, channelId: 2, userId: 14, userName: 'Pedro Gutiérrez', userAvatar: '', userRole: 'listener',
    content: 'Yo voy al meet & greet. Nos vemos allá, llevo mate 🧉',
    timestamp: '2026-06-18T14:30:00', relativeTime: 'Hace 1 h',
    reactions: [
      { emoji: '😂', count: 3, users: ['Sofía Ramírez', 'Camila Torres'], reactedByMe: false },
      { emoji: '❤️', count: 2, users: ['Diego Fernández'], reactedByMe: false },
    ],
    replyTo: null, replyPreview: null, replyUserName: null,
    isPinned: false, isHighlighted: false, edited: false,
  },
  {
    id: 12, channelId: 2, userId: 7, userName: 'Camila Torres', userAvatar: '', userRole: 'listener',
    content: 'Siiii lleven mate. Yo llevo bizcochitos. Esto se pone bueno 🥳',
    timestamp: '2026-06-18T14:32:00', relativeTime: 'Hace 1 h',
    reactions: [
      { emoji: '🔥', count: 5, users: ['Sofía Ramírez', 'Pedro Gutiérrez', 'Diego Fernández'], reactedByMe: false },
    ],
    replyTo: null, replyPreview: null, replyUserName: null,
    isPinned: false, isHighlighted: false, edited: false,
  },
  {
    id: 13, channelId: 2, userId: 1, userName: 'Carlos Herrera', userAvatar: '', userRole: 'admin',
    content: '🚨 AVISO IMPORTANTE: Este domingo 22 es el Día del Padre. Vamos a hacer una transmisión especial de 10 a 22hs. Preparen sus dedicatorias que las leemos todas al aire. ¡No se lo pierdan!',
    timestamp: '2026-06-18T14:45:00', relativeTime: 'Hace 45 min',
    reactions: [
      { emoji: '❤️', count: 15, users: ['Sofía Ramírez', 'Camila Torres', 'Martín Gutiérrez', 'Andrés López', 'Laura Castillo', 'Diego Fernández', 'Pedro Gutiérrez'], reactedByMe: false },
      { emoji: '🔥', count: 10, users: ['Valentina Suárez', 'Roberto Díaz', 'María José López'], reactedByMe: false },
      { emoji: '🙌', count: 8, users: ['Camila Torres', 'Diego Fernández'], reactedByMe: false },
    ],
    replyTo: null, replyPreview: null, replyUserName: null,
    isPinned: true, isHighlighted: true, edited: false,
  },
  {
    id: 14, channelId: 2, userId: 6, userName: 'Sofía Ramírez', userAvatar: '', userRole: 'listener',
    content: 'Qué buena noticia Carlitos. Ya estoy pensando qué canción dedicarle a mi viejo. ¿Se puede mandar por WhatsApp también?',
    timestamp: '2026-06-18T14:48:00', relativeTime: 'Hace 40 min',
    reactions: [
      { emoji: '👍', count: 2, users: ['Carlos Herrera'], reactedByMe: false },
    ],
    replyTo: 13, replyPreview: '🚨 AVISO IMPORTANTE: Este domingo 22 es el Día del Padre...', replyUserName: 'Carlos Herrera',
    isPinned: false, isHighlighted: false, edited: false,
  },
  {
    id: 15, channelId: 2, userId: 1, userName: 'Carlos Herrera', userAvatar: '', userRole: 'admin',
    content: 'Sí Sofi, WhatsApp y también por acá en el chat. Todas las plataformas habilitadas. La idea es que todos puedan participar.',
    timestamp: '2026-06-18T14:50:00', relativeTime: 'Hace 35 min',
    reactions: [
      { emoji: '❤️', count: 3, users: ['Sofía Ramírez'], reactedByMe: false },
    ],
    replyTo: 14, replyPreview: 'Qué buena noticia Carlitos. Ya estoy pensando qué canción...', replyUserName: 'Sofía Ramírez',
    isPinned: false, isHighlighted: false, edited: false,
  },
  {
    id: 16, channelId: 2, userId: 4, userName: 'Diego Martínez', userAvatar: '', userRole: 'mod',
    content: '⚠️ Recordatorio: mantengamos el chat limpio y respetuoso. Cualquier mensaje inapropiado será eliminado. Gracias a todos por hacer de esta comunidad un lugar genial.',
    timestamp: '2026-06-18T14:55:00', relativeTime: 'Hace 30 min',
    reactions: [
      { emoji: '👏', count: 6, users: ['Valentina Suárez', 'Carlos Herrera', 'María José López'], reactedByMe: false },
    ],
    replyTo: null, replyPreview: null, replyUserName: null,
    isPinned: false, isHighlighted: false, edited: false,
  },
  {
    id: 17, channelId: 2, userId: 8, userName: 'Martín Gutiérrez', userAvatar: '', userRole: 'listener',
    content: 'Che, ¿y el concurso sorpresa que habían dicho la semana pasada? Al final qué pasó con eso?',
    timestamp: '2026-06-18T15:00:00', relativeTime: 'Hace 25 min',
    reactions: [],
    replyTo: null, replyPreview: null, replyUserName: null,
    isPinned: false, isHighlighted: false, edited: false,
  },
  {
    id: 18, channelId: 2, userId: 2, userName: 'Valentina Suárez', userAvatar: '', userRole: 'host',
    content: '¡Martín! Está todo listo, lo anunciamos en unos minutos al aire. Estate atento que hay 2 pases VIP para el Festival del Parque Central. Va a estar bueno 🎉',
    timestamp: '2026-06-18T15:02:00', relativeTime: 'Hace 23 min',
    reactions: [
      { emoji: '🔥', count: 11, users: ['Martín Gutiérrez', 'Camila Torres', 'Sofía Ramírez', 'Andrés López', 'Laura Castillo'], reactedByMe: false },
      { emoji: '😮', count: 6, users: ['Diego Fernández', 'Pedro Gutiérrez'], reactedByMe: false },
    ],
    replyTo: 17, replyPreview: 'Che, ¿y el concurso sorpresa que habían dicho...', replyUserName: 'Martín Gutiérrez',
    isPinned: false, isHighlighted: false, edited: false,
  },
  {
    id: 19, channelId: 2, userId: 9, userName: 'Laura Castillo', userAvatar: '', userRole: 'listener',
    content: 'Yo quiero esos pases. El año pasado me quedé afuera por un pelo. Esta vez no se me escapan 😤',
    timestamp: '2026-06-18T15:05:00', relativeTime: 'Hace 20 min',
    reactions: [
      { emoji: '😂', count: 5, users: ['Camila Torres', 'Martín Gutiérrez'], reactedByMe: false },
      { emoji: '🔥', count: 3, users: ['Sofía Ramírez'], reactedByMe: false },
    ],
    replyTo: null, replyPreview: null, replyUserName: null,
    isPinned: false, isHighlighted: false, edited: false,
  },
  {
    id: 20, channelId: 2, userId: 3, userName: 'Roberto Díaz', userAvatar: '', userRole: 'host',
    content: 'Último bloque del programa, gente. Gracias por bancar toda la tarde. Nos vemos mañana a las 14hs. ¡Los quiero! ❤️🎙️',
    timestamp: '2026-06-18T15:28:00', relativeTime: 'Hace 2 min',
    reactions: [
      { emoji: '❤️', count: 14, users: ['Camila Torres', 'Sofía Ramírez', 'Martín Gutiérrez', 'Andrés López', 'Laura Castillo', 'Diego Fernández', 'Pedro Gutiérrez'], reactedByMe: false },
      { emoji: '👏', count: 9, users: ['Valentina Suárez', 'Carlos Herrera', 'María José López'], reactedByMe: false },
      { emoji: '🔥', count: 7, users: ['Andrés López', 'Camila Torres'], reactedByMe: false },
    ],
    replyTo: null, replyPreview: null, replyUserName: null,
    isPinned: false, isHighlighted: false, edited: false,
  },
];

/* ─── Helper ─── */
export function getCommunityChatData(channelId: number) {
  const messages = chatMessages.filter((m) => m.channelId === channelId);
  const members = chatMembers;
  const categories = channelCategories;
  return { messages, members, categories };
}